import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CalendarDays, Sparkles, ChevronRight, Play } from 'lucide-react';
import { getSpeakers } from '../services/api';

const API = process.env.REACT_APP_BACKEND_URL;

const slugify = (n) =>
  (n || '').toLowerCase().replace(/['\u2019’]/g, '').replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');

const daysLabel = (n) => {
  if (n === 0) return 'today';
  if (n === 1) return 'tomorrow';
  return `in ${n} days`;
};

/**
 * Single-line compact strip — the ENTIRE row scrolls horizontally, so on
 * mobile the user can swipe left and slide the kicker/title out of view to
 * reveal every recording chip (finally reaching the "See all" tail).
 *
 * Layout:
 *   [Icon · KICKER · TITLE · N shiurim →] [chip][chip][chip]…[See all]
 *   └─────────── all in one overflow-x-auto strip ────────────────┘
 *
 * The title-block is a pill-styled chip itself (tap → full page); the
 * recording chips follow. On very narrow screens the title is allowed to
 * wrap onto TWO short lines instead of stretching wider than the viewport.
 */
const CompactRow = ({
  icon: Icon,
  kicker,
  title,
  count,
  recs,
  navigate,
  viewAllHref,
  testid,
}) => {
  if (!recs || recs.length === 0) return null;
  const total = typeof count === 'number' && count > 0 ? count : recs.length;
  return (
    // Feb-2026 (user: "make it if i press anywhere random on the bar …
    // let it all act as a button to bring me wherever see all would
    // bring me"): the ENTIRE row is now a click target. The inner chips
    // stopPropagation on their own onClicks so playback still works.
    // We used to layer an invisible absolute button under a z-10 flex,
    // but the flex swallowed empty-space clicks — moving the handler
    // to the outer wrapper closes that gap.
    <div
      role="button"
      tabIndex={0}
      onClick={() => viewAllHref && navigate(viewAllHref)}
      onKeyDown={(e) => {
        if ((e.key === 'Enter' || e.key === ' ') && viewAllHref) {
          e.preventDefault();
          navigate(viewAllHref);
        }
      }}
      aria-label={viewAllHref ? `See all ${title}` : undefined}
      className="relative rounded-lg bg-white border border-[#e1ddd6] shadow-sm dark:bg-[hsl(222,40%,14%)] dark:border-white/10 hover:border-[#9a7b24]/40 transition-colors cursor-pointer focus:outline-none focus:ring-2 focus:ring-[#9a7b24]/50"
      data-testid={testid}
    >
      <div
        className="relative flex items-center gap-1.5 overflow-x-auto scrollbar-none px-2 py-2 [-webkit-overflow-scrolling:touch]"
        style={{ WebkitOverflowScrolling: 'touch' }}
        data-testid={`${testid}-strip`}
      >
        {/* Label chip — first thing in the scroll strip, scrolls off with the row */}
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); viewAllHref && navigate(viewAllHref); }}
          className="shrink-0 inline-flex items-center gap-1.5 px-2 py-1 rounded-full bg-[#ffffff] border border-[#e1ddd6] hover:bg-[#0f1729] hover:text-[#e5c976] transition-colors group/label dark:bg-white/5 dark:border-white/10"
          data-testid={`${testid}-title`}
          title={title}
        >
          <Icon className="w-3.5 h-3.5 text-[#9a7b24] shrink-0" />
          <span className="text-[#9a7b24] uppercase tracking-[0.14em] text-[9px] font-semibold shrink-0">
            {kicker}
          </span>
          <span className="text-[11px] font-bold text-[#0f1729] dark:text-white leading-tight max-w-[42vw] sm:max-w-none truncate group-hover/label:text-[#e5c976]">
            {title}
          </span>
          {/* Jun-2026 (user: "numbers in the little gold circle are not
              centered on iphone"): iOS Safari resolves the digit's default
              line-height taller than the padded box, pushing it off-centre.
              A fixed square with leading-none + tabular-nums centres the
              glyph identically on every device. */}
          <span
            className="shrink-0 inline-flex items-center justify-center text-[10px] font-semibold text-white bg-[#9a7b24] rounded-full leading-none tabular-nums text-center px-0"
            style={{
              height: '18px',
              minWidth: '18px',
              paddingLeft: String(total).length > 2 ? '4px' : 0,
              paddingRight: String(total).length > 2 ? '4px' : 0,
            }}
            data-testid={`${testid}-count`}
          >
            {total}
          </span>
        </button>

        {/* Recording chips */}
        {recs.map((r) => (
          <button
            key={r.drive_file_id}
            type="button"
            onClick={(e) => { e.stopPropagation(); navigate(`/player/${r.drive_file_id}`, { state: { queue: recs } }); }}
            className="group/chip inline-flex items-center gap-1.5 shrink-0 max-w-[220px] px-2.5 py-1 rounded-full bg-[#ffffff] border border-[#e1ddd6] hover:bg-[#0f1729] hover:border-[#0f1729] transition-colors dark:bg-white/5 dark:border-white/10 dark:hover:bg-[#9a7b24] dark:hover:border-[#9a7b24]"
            title={`${r.title} — ${r.speaker}`}
            data-testid={`${testid}-chip-${r.drive_file_id}`}
          >
            <Play className="w-2.5 h-2.5 text-[#9a7b24] group-hover/chip:text-[#e5c976] shrink-0" />
            <span className="text-[11px] text-[#0f1729] group-hover/chip:text-[#e5c976] truncate dark:text-white/80 dark:group-hover/chip:text-white">
              {r.title}
            </span>
          </button>
        ))}

        {viewAllHref && (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); navigate(viewAllHref); }}
            className="inline-flex items-center gap-1 shrink-0 text-[11px] font-medium text-[#9a7b24] hover:text-[#7d6119] transition-colors px-2 py-1"
            data-testid={`${testid}-cta`}
          >
            See all
            <ChevronRight className="w-3 h-3" />
          </button>
        )}
      </div>
    </div>
  );
};

const CACHE_KEY = 'vaad_upcoming_hero_cache_v1';
const readCache = () => {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    // Stale after 6h — the endpoint only really changes when the
    // calendar day rolls over.
    if (!parsed?.ts || Date.now() - parsed.ts > 6 * 60 * 60 * 1000) return null;
    return parsed.data;
  } catch { return null; }
};
const writeCache = (data) => {
  try { localStorage.setItem(CACHE_KEY, JSON.stringify({ ts: Date.now(), data })); } catch { /* noop */ }
};

const UpcomingHero = () => {
  const navigate = useNavigate();
  // Feb-2026 (user: "fix bug where This Shabbos and Coming Up don't
  // immediately appear"): seed from a 6-hour localStorage cache so
  // the strip renders on the FIRST paint of every subsequent visit
  // instead of waiting on the /api/calendar/upcoming round-trip.
  const [data, setData] = useState(() => readCache());
  const [speakersWithPics, setSpeakersWithPics] = useState(null);
  const [loading, setLoading] = useState(() => !readCache());

  useEffect(() => {
    // Feb-2026 (user: "allow me to keep on scrolling to the right in
    // coming up and this parsha as long as a re more recs without having
    // to press see all"): backend caps `limit_each` at 100 (Pydantic
    // validation returns an empty payload above that — earlier bump to
    // 500 accidentally nuked both bars). 100 comfortably covers even
    // the fattest parsha/holiday shortlist we have today (~10-30 recs).
    fetch(`${API}/api/calendar/upcoming?limit_each=100`)
      .then((r) => r.json())
      .then((d) => { setData(d); writeCache(d); })
      .catch(() => { /* keep cached data, if any */ })
      .finally(() => setLoading(false));
    // Feb-2026 (user): Up Next chips should only show recordings whose
    // speaker has a profile pic. Pull the home-speakers list once and
    // filter client-side. Uses shared getSpeakers() so concurrent
    // callers on the same page share ONE network request.
    getSpeakers()
      .then((list) => {
        const s = new Set();
        (list || []).forEach((sp) => {
          if (sp && sp.image && typeof sp.image === 'string' && (sp.image.startsWith('http') || sp.image.startsWith('/'))) {
            s.add(sp.name);
          }
        });
        setSpeakersWithPics(s);
      })
      .catch(() => setSpeakersWithPics(new Set()));
  }, []);

  if (loading && !data) {
    return (
      <div className="rounded-lg bg-white border border-[#e1ddd6] h-10 animate-pulse dark:bg-[hsl(222,40%,14%)] dark:border-white/10" />
    );
  }
  if (!data) return null;

  const shabbos = data.this_shabbos;
  const holiday = data.next_holiday;

  // Filter chips to speakers with a profile pic; if that ends up empty,
  // fall back to the unfiltered list so users still see something.
  const filterRecs = (recs) => {
    if (!speakersWithPics || speakersWithPics.size === 0) return recs;
    const filtered = (recs || []).filter((r) => speakersWithPics.has(r.speaker));
    return filtered.length > 0 ? filtered : recs;
  };

  return (
    <section className="mb-3 space-y-2" data-testid="upcoming-hero">
      {shabbos && data.parsha_recommendations?.length > 0 && (
        <CompactRow
          icon={CalendarDays}
          kicker="This Shabbos"
          title={`Parshas ${shabbos.name}`}
          count={data.parsha_total}
          recs={filterRecs(data.parsha_recommendations)}
          viewAllHref={`/parsha/${shabbos.slug}`}
          navigate={navigate}
          testid="upcoming-parsha"
        />
      )}
      {holiday && data.holiday_recommendations?.length > 0 && (
        <CompactRow
          icon={Sparkles}
          kicker="Coming up"
          title={`${holiday.name} · ${daysLabel(holiday.days_until)}`}
          count={data.holiday_total}
          recs={filterRecs(data.holiday_recommendations)}
          viewAllHref={holiday.topic ? `/topic/${slugify(holiday.topic)}` : '/upcoming'}
          navigate={navigate}
          testid="upcoming-holiday"
        />
      )}
    </section>
  );
};

export default UpcomingHero;
